import { type ReactNode, useMemo } from 'react';
import {
  FeatureManagementProvider as FeatureManagementProviderComponent,
} from '@printdeal/feature-management/react';
import { useSelector } from 'react-redux';
import { useBreakpoint } from '@printdeal/ui-library';
import { useIntl } from 'react-intl';
import { useSegmentation } from '@printdeal/segmentation/react';
import { windowPresent } from '../../helpers/window';
import { getStore } from '../../helpers/env';
import { getConfiguration } from '../../helpers/Configuration';
import { isRecognizedUser } from '../../templates/homepage/homepage-helper';

const getTier = () => {
  if (!windowPresent) {
    return 'development';
  }

  if (window.navigator.userAgent.includes('Playwright')) {
    return 'test';
  }

  return window.location.hostname === getConfiguration().channel.url ? 'production' : 'development';
};

interface Props {
  children: ReactNode;
}

const FeatureManagementProvider = ({ children }: Props) => {
  const { locale } = useIntl();
  const { accountEmail, isCompany, accountId } = useSelector<{
    customer: {
      account: { id: string, email: string },
      customer: { custom?: { fields: { isCompany?: string } } }
    },
  }, {
    accountId?: string,
    accountEmail?: string,
    isCompany?: string,
  }>(({ customer }) => ({
    accountId: customer.account.id || '',
    accountEmail: customer.account.email,
    isCompany: customer.customer.custom?.fields.isCompany,
  }));
  const isDesktop = useBreakpoint('lg');
  const { data: segmentationData } = useSegmentation();

  const user = useMemo(() => {
    const customerSegmentAttributes = Object.create(null);

    if (segmentationData) {
      const { traits } = segmentationData;
      if (traits.customerSegmentLevel0) {
        customerSegmentAttributes.customerSegmentLevel0 = traits.customerSegmentLevel0;
      }
      if (traits.customerSegmentLevel1) {
        customerSegmentAttributes.customerSegmentLevel1 = traits.customerSegmentLevel1;
      }
      if (traits.customerSegmentLevel2) {
        customerSegmentAttributes.customerSegmentLevel2 = traits.customerSegmentLevel2;
      }
    }

    return ({
      email: accountEmail || undefined,
      store: getStore(),
      locale,
      custom: {
        isRecognized: isRecognizedUser(accountId || ''),
        device: isDesktop ? 'desktop' : 'mobile',
        isLoggedIn: Boolean(accountEmail),
        isCompany: Boolean(isCompany),
        audiences: segmentationData?.audiences,
        isReseller: segmentationData?.isReseller || false,
        isEnduser: segmentationData?.isEnduser || false,
        ...customerSegmentAttributes,
      },
    });
  }, [locale, accountEmail, isCompany, isDesktop, segmentationData, accountId]);

  if (!process.env.GATSBY_STATSIG_CLIENT_KEY) {
    throw new Error('Environment variable "GATSBY_STATSIG_CLIENT_KEY" is not set.');
  }

  return (
    <FeatureManagementProviderComponent
      user={user}
      apiKey={process.env.GATSBY_STATSIG_CLIENT_KEY}
      environment={getTier()}
    >
      {children}
    </FeatureManagementProviderComponent>
  );
};

export default FeatureManagementProvider;
